import React, { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload, X, Image as ImageIcon, Video, FileText,
  Loader2, CheckCircle2, AlertCircle, Pause, Play,
} from "lucide-react";
import { upInit, upPart, upComplete, upCancel } from "../../lib/api";

const CHUNK_SIZE = 8 * 1024 * 1024;

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

function fileIcon(f) {
  const t = f.type || "";
  if (t.startsWith("image/")) return ImageIcon;
  if (t.startsWith("video/")) return Video;
  return FileText;
}

/**
 * ChunkedUploader — Drag-and-Drop + Chunked Multi-Upload via /api/dataroom/uploads/*.
 *
 * Props:
 *   kategorie: string — Default-Kategorie, in die alle Files landen
 *   onUploaded: (fileDoc) => void — Callback nach erfolgreichem Upload
 *   accept: string — MIME/Extension-Filter, default "image/*,video/*"
 *   multiple: bool — default true
 *   compact: bool — kleinere UI ohne Drop-Zone-Beschriftung
 *   testidPrefix: string
 */
export default function ChunkedUploader({
  kategorie,
  onUploaded,
  accept = "image/*,video/*",
  multiple = true,
  compact = false,
  testidPrefix = "chunked-uploader",
}) {
  const [items, setItems] = useState([]); // {file, key, status, progress, error, uploadId, paused}
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const pausedRefs = useRef({});

  const updateItem = useCallback((key, patch) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, []);

  const uploadOne = async (item) => {
    const key = item.key;
    try {
      // 1. Init
      const init = await upInit({
        filename: item.file.name,
        size_bytes: item.file.size,
        content_type: item.file.type || "application/octet-stream",
        kategorie,
      });
      updateItem(key, { uploadId: init.upload_id, status: "uploading" });

      const totalParts = init.total_parts;
      for (let n = 1; n <= totalParts; n++) {
        // Pause-Loop
        while (pausedRefs.current[key]) {
          await new Promise((r) => setTimeout(r, 500));
        }

        const start = (n - 1) * CHUNK_SIZE;
        const end = Math.min(item.file.size, start + CHUNK_SIZE);
        const blob = item.file.slice(start, end);
        // Retry mit Exponential Backoff bei Netzwerkfehler (3 Versuche)
        let lastErr = null;
        let success = false;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            await upPart(init.upload_id, n, blob);
            success = true;
            break;
          } catch (err) {
            lastErr = err;
            if (attempt < 2) {
              await new Promise((r) => setTimeout(r, 1500 * Math.pow(2, attempt)));
            }
          }
        }
        if (!success) throw (lastErr || new Error("Part-Upload fehlgeschlagen"));
        const pct = Math.round((n / totalParts) * 92); // bis 92% für Parts, Rest = Verarbeitung
        updateItem(key, { progress: pct });
      }

      // 2. Complete — Backend antwortet jetzt sofort (Background-Task macht den Rest)
      updateItem(key, { status: "assembling", progress: 95 });
      const file = await upComplete(init.upload_id);

      // Backend kommt zurück mit processing=true → File ist in der Galerie sichtbar,
      // Thumbnail/Tigris läuft im Hintergrund. Die /bilder-videos-Seite pollt
      // automatisch alle 4 s.
      updateItem(key, { status: "done", progress: 100, fileId: file.id });
      onUploaded?.(file);
      const wasProc = file.processing === true;
      toast.success(
        wasProc
          ? `Hochgeladen: ${item.file.name} · Vorschau wird erstellt…`
          : `Hochgeladen: ${item.file.name}`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload fehlgeschlagen";
      updateItem(key, { status: "error", error: msg });
      toast.error(`Upload fehlgeschlagen: ${item.file.name} — ${msg}`);
    }
  };

  const startUploads = (newItems) => {
    // Bis zu 4 parallel — guter Trade-off zwischen Speed und Browser-Connection-Limit
    let idx = 0;
    let active = 0;
    const MAX_PARALLEL = 4;
    return new Promise((resolve) => {
      const next = () => {
        while (active < MAX_PARALLEL && idx < newItems.length) {
          const it = newItems[idx++];
          active++;
          uploadOne(it).finally(() => {
            active--;
            if (idx >= newItems.length && active === 0) resolve();
            else next();
          });
        }
      };
      next();
    });
  };

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const list = Array.from(fileList);
    const newItems = list.map((f) => ({
      key: `${f.name}_${f.size}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      file: f,
      status: "queued",
      progress: 0,
      paused: false,
    }));
    setItems((prev) => [...prev, ...newItems]);
    // Kick off uploads
    startUploads(newItems);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onPickClick = () => inputRef.current?.click();

  const togglePause = (item) => {
    pausedRefs.current[item.key] = !pausedRefs.current[item.key];
    updateItem(item.key, { paused: pausedRefs.current[item.key] });
  };

  const cancelOne = async (item) => {
    if (item.uploadId && item.status !== "done") {
      try { await upCancel(item.uploadId); } catch {}
    }
    setItems((prev) => prev.filter((it) => it.key !== item.key));
  };

  return (
    <div className="space-y-3" data-testid={testidPrefix}>
      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={onPickClick}
        data-testid={`${testidPrefix}-dropzone`}
        className={`border-2 border-dashed transition-colors cursor-pointer ${
          dragOver ? "border-gold bg-gold/5" : "border-navy/20 bg-white hover:bg-ledger"
        } ${compact ? "p-4" : "p-8"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={`${testidPrefix}-input`}
        />
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <Upload size={compact ? 18 : 28} className={dragOver ? "text-gold" : "text-navy/55"} />
          <div className={`font-sans ${compact ? "text-xs" : "text-sm"} text-navy`}>
            {compact ? "Dateien wählen / hierherziehen" : "Bilder oder Videos hierher ziehen — oder klicken"}
          </div>
          {!compact && (
            <div className="text-[11px] font-sans text-navy/55">
              JPG, PNG, HEIC, MP4, MOV — bis 3 GB pro Datei · iPhone-Safari unterstützt
            </div>
          )}
        </div>
      </div>

      {/* Liste der Uploads */}
      {items.length > 0 && (
        <ul className="space-y-2" data-testid={`${testidPrefix}-list`}>
          {items.map((it) => {
            const Icon = fileIcon(it.file);
            return (
              <li
                key={it.key}
                data-testid={`${testidPrefix}-item`}
                className="border border-navy/12 bg-white px-3 py-2 flex items-center gap-3"
              >
                <Icon size={16} className="text-navy/55 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-navy truncate font-sans">{it.file.name}</div>
                  <div className="text-[10px] font-sans text-navy/55 tabular-nums">
                    {fmtSize(it.file.size)} ·{" "}
                    {it.status === "queued" && "Warte…"}
                    {it.status === "uploading" && `${it.progress}%`}
                    {it.status === "assembling" && "Wird verarbeitet…"}
                    {it.status === "done" && "Fertig"}
                    {it.status === "error" && (it.error || "Fehler")}
                  </div>
                  {(it.status === "uploading" || it.status === "assembling") && (
                    <div className="h-1 bg-navy/8 mt-1 overflow-hidden">
                      <div
                        className="h-full bg-navy"
                        style={{ width: `${Math.max(2, it.progress)}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {it.status === "uploading" && (
                    <button
                      onClick={() => togglePause(it)}
                      title={it.paused ? "Fortsetzen" : "Pause"}
                      className="p-1 text-navy/55 hover:text-navy"
                      data-testid={`${testidPrefix}-pause`}
                    >
                      {it.paused ? <Play size={14} /> : <Pause size={14} />}
                    </button>
                  )}
                  {it.status === "done" && <CheckCircle2 size={14} className="text-emerald-700" />}
                  {it.status === "error" && <AlertCircle size={14} className="text-tot" />}
                  {(it.status === "queued" || it.status === "uploading" || it.status === "error" || it.status === "done") && (
                    <button
                      onClick={() => cancelOne(it)}
                      title="Entfernen"
                      className="p-1 text-navy/55 hover:text-navy"
                      data-testid={`${testidPrefix}-remove`}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
